import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslate } from 'react-admin';

import {
  PlatformSeries,
  PlatformStats,
  getPlatformSeries,
  getPlatformStats,
} from 'src/pages/admin/services/vaultService';
import { parseApiError } from 'src/pages/admin/utils/decimal-input';

import { SERIES_DAYS } from './constants';
import { dashboardErrorMessage, viewerTimeZone } from './helpers';

/**
 * `GET /vault/dashboard` (the cards) and `GET /vault/dashboard/series` (the charts) for the viewer's own zone. The two
 * reads are independent: a failed series leaves the cards, and the other way round. A read lands only if no newer one
 * has been started (the pattern of useVaults), and a failed read drops the figures it could not refresh rather than
 * keep a stale one: the cards show "—" for them.
 */
export const useDashboard = (timeZone: string = viewerTimeZone()) => {
  const translate = useTranslate();
  // react-admin's translate is not identity-stable; a ref keeps refetch stable (see useVaults).
  const translateRef = useRef(translate);
  translateRef.current = translate;
  const [stats, setStats] = useState<PlatformStats | null>(null);
  const [series, setSeries] = useState<PlatformSeries | null>(null);
  const [statsError, setStatsError] = useState<string | null>(null);
  const [seriesError, setSeriesError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const started = useRef(0);

  const refetch = useCallback(async () => {
    started.current += 1;
    const read = started.current;
    setLoading(true);
    const live = () => read === started.current;
    await Promise.all([
      getPlatformStats(timeZone).then(
        (result) => {
          if (!live()) return;
          setStats(result);
          setStatsError(null);
        },
        (error) => {
          if (!live()) return;
          setStats(null);
          setStatsError(dashboardErrorMessage(parseApiError(error), translateRef.current));
        }
      ),
      getPlatformSeries(SERIES_DAYS, timeZone).then(
        (result) => {
          if (!live()) return;
          setSeries(result);
          setSeriesError(null);
        },
        (error) => {
          if (!live()) return;
          setSeries(null);
          setSeriesError(dashboardErrorMessage(parseApiError(error), translateRef.current));
        }
      ),
    ]);
    if (live()) setLoading(false);
  }, [timeZone]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { stats, series, statsError, seriesError, loading, refetch };
};
